import { useCallback, useEffect, useLayoutEffect, useRef, useState }
  from 'react'
import type { ReactNode } from 'react'
import { HelpIcon } from '../common/HelpIcon'
import { CopyButton } from '../common/CopyButton'
import { Pager } from './Pager'
import type { Column, HideOn, PageState, SortState } from './types'
import s from './DataTable.module.scss'

interface Props<T> {
  columns: Column<T>[]
  rows: T[]
  /** Eindeutig je Zeile. Der Index reicht nicht: Beim Umsortieren wandern die Zeilen. */
  rowKey: (row: T) => string | number
  /**
   * Zusätzliche Attribute je Zeile — für Zustände, die nicht aus den Daten
   * folgen.
   *
   * Gedacht für `data-*`: Eine gerade eingetroffene Zeile bekommt eine
   * Markierung, die das Stylesheet aufgreift. Als Klassenname ginge es auch,
   * dann müsste die Tabelle aber die Namen der Ansicht kennen.
   */
  rowAttrs?: (row: T) => Record<string, string>
  /**
   * Zu welcher Zeile beim ERSTEN Laden gesprungen wird.
   *
   * Die Tabelle springt zur ersten Zeile, für die das Prädikat zutrifft, und
   * lässt ein paar Zeilen Vorlauf darüber stehen — sonst sähe es aus, als
   * gäbe es nichts davor.
   *
   * GENAU EINMAL, beim ersten Satz Zeilen. Nicht beim Filtern, nicht beim
   * Sortieren, nicht beim Nachladen: Wer dort etwas einstellt, schaut auf
   * eine bestimmte Stelle, und ein Sprung risse sie ihm weg.
   *
   * WER springt, entscheidet die Tabelle; WOHIN, die Fachseite. Der
   * Ereigniskalender weiß, dass „heute" der erste Termin ab heute ist — die
   * Tabelle weiß nur, wie man scrollt.
   */
  scrollTo?: (row: T) => boolean
  sort?: SortState
  /**
   * Wird beim Klick auf einen sortierbaren Kopf gerufen.
   *
   * Die Tabelle sortiert NICHT selbst. Bei Paginierung wäre das falsch: Sie
   * sähe nur die aktuelle Seite und würde 10 von 1.248 Zeilen sortieren — das
   * Ergebnis sähe richtig aus und wäre es nicht.
   */
  onSort?: (sort: SortState) => void
  page?: PageState
  onPage?: (page: number) => void
  onRowClick?: (row: T) => void
  loading?: boolean
  /** Was steht da, wenn nichts da ist. */
  empty?: ReactNode
  /** Links in der Statuszeile, anstelle der Bereichsangabe. */
  status?: ReactNode
  /**
   * Wie viele Zeilen STEHEN BLEIBEN. Der Rest ist per Scrollen im
   * Tabellenkoerper erreichbar, ohne dass die Seite wandert.
   *
   * `null` heisst "alle" — dann waechst die Tabelle wie bisher mit ihrem
   * Inhalt und die Seite scrollt.
   *
   * Gemessen und nicht gerechnet: Die Hoehe ergibt sich aus den ERSTEN N
   * Zeilen, wie sie tatsaechlich gerendert wurden. Eine Formel
   * `zeilen * zeilenhoehe` waere falsch, sobald eine Spalte umbricht — und
   * das tun mehrere (`wrap: true`).
   */
  visibleRows?: number | null
  /**
   * Setzt die Zahl. NUR WENN GESETZT erscheint das Auswahlfeld unten —
   * eine Tabelle, deren Hoehe die Ansicht vorgibt, soll niemand daran
   * verstellen koennen.
   */
  onVisibleRows?: (rows: number | null) => void
  /**
   * Wird gerufen, sobald das Ende der Zeilen in Sicht kommt.
   *
   * Die Tabelle laedt NICHT selbst nach: Sie weiss nichts von Seiten,
   * Filtern und Rechten. Sie meldet nur, dass das Ende erreicht ist — was
   * daraufhin geschieht, entscheidet die Ansicht. Dieselbe Trennung wie bei
   * `onSort`, das auch nicht selbst sortiert.
   *
   * Gemeldet wird schon KURZ VOR dem Ende, damit die naechsten Zeilen da
   * sind, bevor jemand auf eine leere Flaeche sieht.
   */
  onEndReached?: () => void
  /** Zeigt unten "laedt weitere ..." an. */
  loadingMore?: boolean
}

/**
 * Die Tabelle.
 *
 * Ein echtes `<table>`: Der Browser richtet Spalten selbst aus, und
 * Bildschirmleser wissen ohne Zutun, welche Überschrift zu welcher Zelle
 * gehört. Die Spalten kommen als Datenstruktur von außen, nicht als JSX — in
 * POC v1 steckten sie samt Breiten im Markup jeder Seite, und genau deshalb
 * war dort keine Responsivität möglich.
 *
 * Auf kleinen Breiten scrollt die Tabelle seitlich, statt Spalten zu opfern:
 * Alle Werte bleiben erreichbar. Die erste Spalte bleibt dabei stehen, damit
 * zuordenbar ist, welche Zeile man liest.
 */
/** Wie viel oberhalb der Zielzeile stehen bleibt — rund zwei Zeilen. */
const VORLAUF_PX = 80

export function DataTable<T>({ columns, rows, rowKey, rowAttrs, scrollTo,
                              sort, onSort,
                              page, onPage, onRowClick, loading, empty,
                              status, visibleRows = null, onVisibleRows,
                              onEndReached, loadingMore }: Props<T>) {
  const scroller = useRef<HTMLDivElement>(null)
  const koerper = useRef<HTMLTableSectionElement>(null)
  const ende = useRef<HTMLDivElement>(null)
  const [moreRight, setMoreRight] = useState(false)

  // Die gemessene Hoehe. `null`, solange nicht gemessen wurde oder alle
  // Zeilen stehen bleiben sollen.
  const [hoehe, setHoehe] = useState<number | null>(null)

  // Prüft, ob rechts noch etwas steht. Ohne diese Kante sieht niemand, dass da
  // weitere Spalten sind — und scrollt nie.
  const check = useCallback(() => {
    const box = scroller.current
    if (!box) return
    setMoreRight(box.scrollLeft + box.clientWidth < box.scrollWidth - 1)
  }, [])

  // Bei jeder Änderung neu messen: Andere Zeilen heißt andere Spaltenbreiten,
  // und ein Fenstergrößenwechsel ändert alles.
  useEffect(() => {
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [check, rows, columns])

  /**
   * Die Hoehe der ersten N Zeilen — nach dem Rendern gemessen.
   *
   * `useLayoutEffect` und nicht `useEffect`: Gemessen wird, was schon im
   * Dokument steht, und die Hoehe muss VOR dem Zeichnen stehen. Sonst zeigte
   * die Tabelle einen Wimpernschlag lang alle Zeilen und schrumpfte dann
   * sichtbar zusammen.
   *
   * Ueber `offsetTop` der Zeile DAHINTER statt ueber die Summe der Hoehen:
   * Das nimmt Rahmen und Abstaende von selbst mit, ohne dass hier stehen
   * muesste, wie das Stylesheet sie setzt.
   *
   * DIE KOPFZEILE KOMMT DAZU. Sie liegt MIT im Scrollkasten — haftend, aber
   * innen —, und `max-height` gilt fuer den ganzen Kasten. Ohne ihren Anteil
   * frisst sie die letzte Zeile: Bei zehn verlangten standen neun da, und die
   * zehnte war angeschnitten.
   */
  useLayoutEffect(() => {
    const body = koerper.current
    if (!visibleRows || !body) { setHoehe(null); return }
    const zeilen = body.rows
    if (zeilen.length <= visibleRows) { setHoehe(null); return }
    const kopf = body.previousElementSibling as HTMLElement | null
    setHoehe((kopf?.offsetHeight ?? 0)
             + zeilen[visibleRows].offsetTop - zeilen[0].offsetTop)
  }, [visibleRows, rows, columns])

  /**
   * Der Beobachter am Ende der Zeilen.
   *
   * `IntersectionObserver` und nicht `onScroll`: Der Browser meldet von
   * selbst, wenn die Marke ins Bild kommt — ohne bei jedem Scrollschritt
   * Positionen zu rechnen. Er greift auch dann, wenn die geladenen Zeilen
   * den Kasten gar nicht fuellen: Dann ist die Marke sofort sichtbar und die
   * Liste laedt weiter, bis sie voll ist.
   *
   * `root` ist der SCROLLKASTEN und nicht das Fenster. Bei fester Hoehe
   * scrollt die Tabelle in sich; gegen das Fenster gemessen waere die Marke
   * dauerhaft sichtbar und die Liste liefe sofort bis zum Ende durch.
   *
   * `rootMargin` gibt den Vorlauf: Gemeldet wird, bevor das Ende wirklich
   * erreicht ist.
   */
  useEffect(() => {
    const marke = ende.current
    const kasten = scroller.current
    if (!marke || !onEndReached) return
    const beobachter = new IntersectionObserver((eintraege) => {
      if (eintraege.some((e) => e.isIntersecting)) onEndReached()
    }, { root: hoehe !== null ? kasten : null, rootMargin: '120px' })
    beobachter.observe(marke)
    return () => beobachter.disconnect()
  }, [onEndReached, hoehe, rows])

  /**
   * Springt beim ersten Laden zur gesuchten Zeile.
   *
   * OHNE ANIMATION. Das ist keine Bewegung, die jemand verfolgen soll,
   * sondern die Ausgangslage — sie soll schon dastehen, wenn man hinsieht.
   * Damit erübrigt sich auch die Rücksicht auf `prefers-reduced-motion`.
   *
   * MIT VORLAUF: Ein paar Zeilen darüber bleiben stehen, sonst sähe der
   * Kalender aus, als begänne er heute. Man soll sehen, dass es davor
   * weitergeht — und dorthin scrollen können.
   */
  const gesprungen = useRef(false)

  /**
   * Sobald jemand SELBST etwas tut, wird nicht mehr gesprungen.
   *
   * Weil "nicht gefunden" jetzt offen bleibt, koennte die Zeile spaeter
   * nachkommen — waehrend man liest. Auf `scroll` zu hoeren taugt dafuer
   * nicht: Den Sprung loest die Tabelle selbst aus. Diese drei kommen nur
   * von aussen.
   */
  const selbstBedient = useRef(false)
  useEffect(() => {
    const kasten = scroller.current
    if (!kasten || !scrollTo) return
    const merken = () => { selbstBedient.current = true }
    for (const art of ['wheel', 'pointerdown', 'keydown'] as const) {
      kasten.addEventListener(art, merken, { passive: true })
    }
    return () => {
      for (const art of ['wheel', 'pointerdown', 'keydown'] as const) {
        kasten.removeEventListener(art, merken)
      }
    }
  }, [scrollTo])

  useEffect(() => {
    if (gesprungen.current || selbstBedient.current
        || !scrollTo || rows.length === 0) return
    const kasten = scroller.current
    if (!kasten) return

    // SOLANGE DER KASTEN NICHT SCROLLEN KANN, WIRD NICHT GESPRUNGEN — und
    // auch nichts abgehakt.
    //
    // Die feste Hoehe entsteht in einem Layout-Effekt und steht erst im
    // naechsten Durchgang am Element. Wer vorher `scrollTop` setzt, schreibt
    // ins Leere: Der Kasten scrollt noch gar nicht, der Wert bleibt 0, und
    // der Sprung waere still ausgefallen. Genau das ist beim ersten Versuch
    // passiert.
    //
    // Fuellen die Zeilen den Kasten nicht, laedt die Liste ohnehin nach, bis
    // sie es tun — dann greift dieser Effekt. Passt am Ende alles hinein,
    // gibt es nichts zu scrollen, und es bleibt zu Recht beim Stillstand.
    if (kasten.scrollHeight <= kasten.clientHeight) return

    // NICHT GEFUNDEN HEISST NICHT "GIBT ES NICHT".
    //
    // Die Liste laedt seitenweise. Steht die gesuchte Zeile erst auf Seite
    // vier, ist sie beim ersten Blick schlicht noch nicht da — und abzuhaken
    // hiesse, nie wieder hinzusehen. Genau das ist im Ereigniskalender
    // passiert: Die erste Seite deckte nur die zurueckliegende Woche ab, der
    // heutige Schnitt kam erst spaeter, und der Sprung fiel aus.
    const treffer = rows.findIndex(scrollTo)
    if (treffer < 0) return

    gesprungen.current = true
    const zeile = koerper.current?.children[treffer] as HTMLElement | undefined
    if (!zeile) return

    // Gegen den KASTEN gemessen, nicht gegen das Fenster: Bei fester Hoehe
    // scrollt die Tabelle in sich, und `scrollIntoView` risse die ganze
    // Seite mit.
    const oben = zeile.getBoundingClientRect().top
                 - kasten.getBoundingClientRect().top
    kasten.scrollTop += oben - VORLAUF_PX
  }, [rows, scrollTo, hoehe])

  function toggleSort(column: Column<T>) {
    if (!column.sortable || !onSort) return
    const same = sort?.key === column.key
    onSort({ key: column.key, descending: same ? !sort?.descending : false })
  }

  return (
    <div className={s.wrap}>
      <div className={s.area}>
        <div className={s.scroll} ref={scroller} onScroll={check}
             data-fest={hoehe !== null}
             style={hoehe !== null ? { maxHeight: hoehe } : undefined}>
          <table className={s.table}>
            <thead>
              <tr>
                {columns.map((column, index) => {
                  const active = sort?.key === column.key
                  return (
                    <th
                      key={column.key}
                      className={[
                        s.th,
                        index === 0 ? s.stickyHead : '',
                        column.sortable ? s.sortable : '',
                        active ? s.sorted : '',
                        column.align === 'right' ? s.right : '',
                        hideClass(column.hideOn),
                      ].filter(Boolean).join(' ')}
                      style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                      // Nur sortierbare Köpfe sind mit der Tastatur erreichbar —
                      // ein Kopf ohne Wirkung wäre eine Station ohne Zweck.
                      tabIndex={column.sortable ? 0 : undefined}
                      aria-sort={active
                        ? (sort!.descending ? 'descending' : 'ascending')
                        : undefined}
                      onClick={() => toggleSort(column)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          toggleSort(column)
                        }
                      }}
                    >
                      <span className={s.thInner}>
                        {column.label}
                        {column.sortable && (
                          <span className={s.arrow} data-active={active} aria-hidden="true">
                            {active && sort!.descending ? '▼' : '▲'}
                          </span>
                        )}
                        {column.help && (
                          <HelpIcon term={column.label}>{column.help}</HelpIcon>
                        )}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>

            <tbody ref={koerper}>
              {rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  {...(rowAttrs?.(row) ?? {})}
                  className={`${s.row} ${onRowClick ? s.clickable : ''}`}
                  // Die ganze Zeile ist das Ziel, nicht ein Link in der ersten
                  // Zelle: Wer eine Zeile anklickt, will den Datensatz — und
                  // trifft mit dem Finger die Zeile viel leichter als ein Wort
                  // darin. Mit der Tastatur führt Enter zum selben Ziel.
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={onRowClick ? (event) => {
                    if (event.key === 'Enter') onRowClick(row)
                  } : undefined}
                >
                  {columns.map((column, index) => (
                    <td
                      key={column.key}
                      className={[
                        s.td,
                        index === 0 ? s.stickyCell : '',
                        column.align === 'right' ? s.right : '',
                        column.mono ? s.mono : '',
                        column.wrap ? s.wrapCell : '',
                        hideClass(column.hideOn),
                      ].filter(Boolean).join(' ')}
                    >
                      {column.copy
                        ? <CellWithCopy row={row} column={column} />
                        : cell(row, column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/*
            Die Marke fuer den Beobachter — IM Scrollkasten, hinter der
            Tabelle. Ausserhalb laege sie hinter dem Kasten und kaeme bei
            fester Hoehe nie in Sicht.

            `aria-hidden`: Fuer einen Bildschirmleser ist sie nichts; das
            Nachladen macht sich ueber die Zeilen bemerkbar, die dazukommen.
          */}
          {onEndReached && (
            <div ref={ende} className={s.ende} aria-hidden="true" />
          )}
          {loadingMore && (
            <div className={s.nachladen} role="status">lädt weitere …</div>
          )}
        </div>

        <div className={s.edge} data-visible={moreRight} aria-hidden="true" />
      </div>

      {loading && <div className={s.loading}>lädt …</div>}
      {!loading && rows.length === 0 && (
        <div className={s.empty}>{empty ?? 'keine Daten'}</div>
      )}

      {(page || status || onVisibleRows) && (
        <div className={s.foot}>
          <span className={s.status}>{status ?? pageRange(rows.length, page)}</span>
          {onVisibleRows && (
            <ZeilenWahl wert={visibleRows} setzen={onVisibleRows} />
          )}
          {page && onPage && <Pager page={page} onPage={onPage} />}
        </div>
      )}
    </div>
  )
}

/**
 * Wie viele Zeilen stehen bleiben.
 *
 * DIE ZAHLEN SIND SICHTHOEHEN, keine Seitengroessen: Sie aendern nichts
 * daran, was geladen wird, nur wie viel davon ohne Scrollen im Bild steht.
 * "alle" ist der Ausgangszustand und laesst die Tabelle wie bisher mit ihrem
 * Inhalt wachsen.
 */
const ZEILENZAHLEN = [10, 15, 25, 50] as const

function ZeilenWahl({ wert, setzen }: {
  wert: number | null
  setzen: (rows: number | null) => void
}) {
  return (
    <label className={s.zeilenwahl}>
      <span>Zeilen</span>
      <select value={wert ?? ''}
              onChange={(event) => setzen(
                event.target.value ? Number(event.target.value) : null)}>
        <option value="">alle</option>
        {ZEILENZAHLEN.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    </label>
  )
}

/**
 * Eine Zelle mit Kopierknopf.
 *
 * Die Anordnung haengt an der Ausrichtung der Spalte: Bei linksbuendigem Text
 * sitzt der Knopf am RECHTEN Zellenrand — der Wert bleibt links stehen, und der
 * Knopf ist immer an derselben Stelle, egal wie lang der Wert ist. Bei
 * rechtsbuendigen Zahlen sitzt er direkt VOR dem Wert, weil die Ziffern rechts
 * untereinander bleiben muessen; dort waere ein Knopf hinter der Zahl genau
 * die Spalte, an der man beim Lesen entlangfaehrt.
 */
function CellWithCopy<T>({ row, column }: { row: T; column: Column<T> }) {
  const value = typeof column.copy === 'function'
    ? column.copy(row)
    : String((row as Record<string, unknown>)[column.key] ?? '')

  const button = <CopyButton value={value} label={column.label} />

  return (
    <span className={s.cellInner} data-align={column.align ?? 'left'}>
      {column.align === 'right' && button}
      <span className={s.cellValue}>{cell(row, column)}</span>
      {column.align !== 'right' && button}
    </span>
  )
}


function hideClass(hideOn?: HideOn): string {
  if (hideOn === 'mobile') return s.hideMobile
  if (hideOn === 'tablet') return s.hideTablet
  return ''
}

function cell<T>(row: T, column: Column<T>): ReactNode {
  if (column.render) return column.render(row)
  const value = (row as Record<string, unknown>)[column.key]
  return value == null ? '—' : String(value)
}

/**
 * Die Bereichsangabe: `51–100 von 1.248`.
 *
 * Ohne Gesamtzahl bleibt es bei `51–100`. Das ist der Normalfall bei großen
 * Tabellen: Ein exaktes `count(*)` kostet dort spürbar Zeit, und das Backend
 * liefert es nur auf Anfrage. `total: null` heißt "nicht gezählt" und nicht
 * "null Treffer" — eine `0` an dieser Stelle wäre eine Falschaussage.
 */
function pageRange(shown: number, page?: PageState): string {
  if (!page || shown === 0) return ''
  const from = (page.page - 1) * page.size + 1
  const to = from + shown - 1
  const total = page.total == null ? '' : ` von ${page.total.toLocaleString('de-DE')}`
  return `${from.toLocaleString('de-DE')}–${to.toLocaleString('de-DE')}${total}`
}
